import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import TripMap from '../components/booking/TripMap';
import AppStoreLink from '../components/common/AppStoreLink';
import { getTripDetail } from '../services/api/bookings';
import { formatRand } from '../config/rateCard';
import { decodePolyline } from '../utils/polyline';
import { formatRoute, statusToHuman } from '../utils/tripFormatting';
import {
  attachmentKind,
  formatDistanceKm,
  formatDurationMinutes,
  formatTripSchedule,
  formatVehicleType,
  normaliseAttachments,
  paymentNotice,
  tripStage,
} from '../utils/tripDetail';
import type { TripAttachment, TripDetail, TripDetailDriver } from '../types/booking';
import '../styles/route.css';

/**
 * One trip, in full — the web counterpart of the app's "Offer Status" screen.
 *
 * Serves all three lanes, as that screen does: scheduled, in progress and past
 * all reach it. The record of a delivery does not change shape because of where
 * the customer is standing, and three near-identical pages would be three
 * places to fix the next time the backend adds a field.
 *
 * Same facts, same order, same words as the app: the route drawn from the
 * trip's own polyline, then the reference, what happened to the payment, when
 * it runs, what carries it, the load notes, the money-distance-duration pills,
 * the files and the drivers. A customer who checks a delivery on their phone
 * and then on a laptop must not have to work out whether they are looking at
 * the same trip.
 *
 * What DOES change with the stage is what the page may offer. Only a live trip
 * gets a link to the tracking map — a finished one has nothing left to watch,
 * and a scheduled one has no driver to watch yet, so both would send the
 * customer to a map reporting the trip is not in progress.
 *
 * Read-only, like the rest of the account area. The app's version of this
 * screen can reschedule, edit and unlink drivers; none of that is offered here,
 * so the page never shows a control it cannot honour.
 */

type LoadState = 'loading' | 'ready' | 'missing' | 'error';

/** The eyebrow names the lane the trip is in, in the lanes' own words. */
const STAGE_EYEBROW = {
  finished: 'Trip archive',
  scheduled: 'Scheduled delivery',
  live: 'Delivery in progress',
} as const;

const TripRecordPage: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  /* The booking reference travels in the query because the backend resolves
     this endpoint by either key, and every lane's rows carry both. Passing it
     through means a trip whose id has been superseded still resolves, exactly
     as it does in the app. */
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  const [state, setState] = useState<LoadState>('loading');
  const [trip, setTrip] = useState<TripDetail | null>(null);

  const load = useCallback(async () => {
    if (!tripId) {
      setState('missing');
      return;
    }

    setState('loading');

    try {
      const detail = await getTripDetail(tripId, code);
      if (!detail) {
        setState('missing');
        return;
      }

      setTrip(detail);
      setState('ready');
    } catch {
      setState('error');
    }
  }, [code, tripId]);

  useEffect(() => {
    void load();
  }, [load]);

  /* The endpoints come out of the route itself. This endpoint returns no
     pickup/drop-off coordinates — only the encoded line and the short address
     strings — so the app derives the pins from the first and last decoded
     points, and so does this. */
  const points = useMemo(
    () => decodePolyline(trip?.polyline ?? trip?.pollyLine),
    [trip?.polyline, trip?.pollyLine],
  );
  const pickup = points.length ? points[0] : null;
  const dropoff = points.length ? points[points.length - 1] : null;

  const attachments = useMemo(() => (trip ? normaliseAttachments(trip) : []), [trip]);
  const notice = trip ? paymentNotice(trip) : null;
  const route = formatRoute(trip?.pickupShort, trip?.dropoffShort);
  const drivers = trip?.drivers ?? [];
  const fare = trip?.fare?.finalAmount ?? trip?.fare?.estimated ?? null;
  const duration = formatDurationMinutes(trip?.durationMin);
  const stage = trip ? tripStage(trip) : null;

  return (
    <div className="landing-shell">
      <Header />
      <main>
        <section className="sw-section">
          <div className="container">
            <div className="sw-section-head">
              <span className="sw-eyebrow">{stage ? STAGE_EYEBROW[stage] : 'Delivery'}</span>
              {/* The route is the heading, not the status. It is what a
                  customer scanning their deliveries recognises one by; the
                  status is a single word and sits as a chip beside the
                  reference below, where the app puts it too. */}
              <h1>{route ?? trip?.code ?? 'Trip details'}</h1>
            </div>

            {state === 'loading' ? <p className="sw-trip-note">Loading this trip…</p> : null}

            {state === 'missing' ? (
              <div>
                <p className="sw-trip-note">
                  We could not find this trip. It may have been removed, or the link may be out of
                  date.
                </p>
                <div className="sw-hero-actions sw-trip-actions">
                  <Link className="sw-btn-secondary" to="/account">
                    Back to your deliveries
                  </Link>
                </div>
              </div>
            ) : null}

            {state === 'error' ? (
              <div>
                <p className="sw-trip-alert">
                  We could not load this trip right now. Nothing about the delivery has changed.
                </p>
                <div className="sw-hero-actions sw-trip-actions">
                  <button className="sw-btn-secondary" onClick={() => void load()} type="button">
                    Try again
                  </button>
                  <Link className="sw-link-action" to="/account">
                    Back to your deliveries
                  </Link>
                </div>
              </div>
            ) : null}

            {state === 'ready' && trip ? (
              <>
                {/* The route first, as on the app screen. When the trip carries
                    no geometry the map is left out rather than drawn empty — a
                    blank frame reads as a map that failed, and every fact it
                    would have shown is written below it anyway. */}
                {points.length >= 2 ? (
                  <TripMap
                    dropoff={dropoff}
                    label={`Map of the route from ${trip.pickupShort} to ${trip.dropoffShort}`}
                    pickup={pickup}
                    routePolyline={trip.polyline ?? trip.pollyLine}
                  />
                ) : (
                  <p className="sw-trip-note">
                    No route has been recorded for this trip. The details below are complete.
                  </p>
                )}

                <div className="sw-trip-panel">
                  <div className="sw-trip-panel-head">
                    <span className="sw-trip-code">{trip.code}</span>
                    <span className="sw-pill sw-pill-quiet">{statusToHuman(trip.status)}</span>
                  </div>

                  {notice ? (
                    <p className={`sw-trip-banner sw-trip-banner-${notice.tone}`}>{notice.message}</p>
                  ) : null}

                  <dl className="sw-trip-facts">
                    {/* Two different facts, and collapsing them loses one. The
                        app shows only the pickup time, where "ASAP" is enough
                        because the trip is in front of you; in an archive it
                        answers nothing, so the booking date is stated beside
                        it rather than substituted for it. */}
                    <div>
                      <dt>Pickup time</dt>
                      <dd>{formatTripSchedule(trip.scheduledAt)}</dd>
                    </div>
                    <div>
                      <dt>Booked</dt>
                      <dd>{formatTripSchedule(trip.createdAt)}</dd>
                    </div>
                    <div>
                      <dt>Vehicle</dt>
                      <dd>{formatVehicleType(trip.vehicleType)}</dd>
                    </div>
                    <div>
                      <dt>Load notes</dt>
                      <dd>{trip.loadNotes?.trim() ? trip.loadNotes : '—'}</dd>
                    </div>
                  </dl>

                  <div className="sw-pill-row">
                    {/* A trip that has not run yet only has an estimate, and it
                        is labelled as one. An unqualified figure on a scheduled
                        delivery is a price promise we have not made. */}
                    {fare != null ? (
                      <span className="sw-pill sw-pill-fare">
                        {formatRand(Math.round(fare))}
                        {trip.fare?.finalAmount == null ? ' est.' : ''}
                      </span>
                    ) : null}
                    <span className="sw-pill sw-pill-quiet">
                      {formatDistanceKm(trip.distanceKm)}
                    </span>
                    {duration ? <span className="sw-pill sw-pill-quiet">{duration}</span> : null}
                  </div>
                </div>

                <TripAttachments attachments={attachments} />
                <TripDrivers drivers={drivers} />

                <div className="sw-hero-actions sw-trip-actions">
                  <Link className="sw-btn-secondary" to="/account">
                    All deliveries
                  </Link>
                  {/* Only a live trip. A finished one has nothing left to
                      watch and a scheduled one has no driver yet, so for either
                      this link would open a map that reports the trip is not in
                      progress — an action that leads nowhere. */}
                  {stage === 'live' ? (
                    <Link
                      className="sw-link-action"
                      to={`/account/trips/${encodeURIComponent(trip.tripId)}`}
                    >
                      Track this trip &rarr;
                    </Link>
                  ) : null}
                  {/* Rescheduling, messaging a driver and invoices all live in
                      the app. Naming them is the honest version of a page that
                      cannot do them. */}
                  <AppStoreLink className="sw-link-action" placement="trip_record">
                    {stage === 'finished'
                      ? 'Rebook or message in the app'
                      : 'Message or cancel in the app'}
                  </AppStoreLink>
                </div>
              </>
            ) : null}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

/**
 * Files uploaded against the trip.
 *
 * Each one opens in a new tab rather than downloading: the URLs are signed and
 * short-lived, and a browser-initiated download of an expired link fails
 * silently, whereas a tab shows whatever the storage service says.
 */
const TripAttachments: React.FC<{ attachments: TripAttachment[] }> = ({ attachments }) => {
  if (!attachments.length) return null;

  return (
    <section className="sw-trip-panel">
      <div className="sw-trip-panel-head">
        <h2 className="sw-choice-title">Trip attachments</h2>
        <span className="sw-pill sw-pill-live">{attachments.length}</span>
      </div>
      <p className="sw-choice-copy">Photos and documents uploaded with this trip.</p>

      <ul className="sw-attachment-list">
        {attachments.map((attachment) => (
          <li key={attachment.id ?? attachment.url}>
            <a
              className="sw-attachment"
              href={attachment.url ?? '#'}
              rel="noreferrer noopener"
              target="_blank"
            >
              <span className="sw-attachment-kind">{attachmentKind(attachment)}</span>
              <span className="sw-attachment-name">
                {attachment.originalName ?? 'Attachment'}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
};

/**
 * Who carried it.
 *
 * Names and standing only — no call button, no message thread. Both need the
 * app, and a driver's phone number is not ours to publish on a web page that
 * outlives the trip.
 */
const TripDrivers: React.FC<{ drivers: TripDetailDriver[] }> = ({ drivers }) => {
  if (!drivers.length) return null;

  return (
    <section className="sw-trip-panel">
      <div className="sw-trip-panel-head">
        <h2 className="sw-choice-title">
          {drivers.length === 1 ? 'Assigned driver' : 'Assigned drivers'}
        </h2>
        <span className="sw-pill sw-pill-live">{drivers.length}</span>
      </div>

      <ul className="sw-driver-list">
        {drivers.map((driver) => (
          <li className="sw-driver-row" key={driver.driverId}>
            <span className="sw-driver-name">{driver.name?.trim() || 'Driver'}</span>
            <span className="sw-driver-meta">
              {statusToHuman(driver.status)}
              {/* One decimal, and only when a rating exists — "0.0" for an
                  unrated driver is a bad review the customer never gave. */}
              {driver.rating != null ? ` · ${driver.rating.toFixed(1)} ★` : ''}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default TripRecordPage;
